import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, User, LogOut } from "lucide-react";
import { useAuthStore, useToast } from "@/stores";
import { cloudBaseAuth } from '@/services/cloudbase/authService';
import { isLocalSession } from '@/services/localAuth';
import { Button } from "@/components/common";
import { APP_NAME, DOCS_URL } from "@/config/constants";

interface HeaderProps {
  onMenuClick?: () => void;
  showMenu?: boolean;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, showMenu = false }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuthStore();
  const toast = useToast();
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    try {
      if (isLocalSession()) await cloudBaseAuth.logout();
      logout();
      navigate("/auth/login");
    } catch {
      toast.error('退出失败，请稍后重试');
    }
  };

  return (
    <header className="bg-warm-bg sticky top-0 z-40">
      <div className="w-full px-6 lg:px-8">
        <div className="max-w-7xl mx-auto h-16 grid grid-cols-3 items-center md:flex md:items-center">
          {/* Left: Menu */}
          <div className="flex items-center md:gap-8">
            {showMenu && (
              <button
                onClick={onMenuClick}
                className="p-2 rounded-lg hover:bg-warm-sand/30 transition-colors lg:hidden"
                aria-label="打开菜单"
              >
                <Menu className="w-6 h-6" />
              </button>
            )}
          </div>

          {/* Center: Logo */}
          <Link
            to="/"
            className="flex items-center justify-center justify-self-center md:justify-start md:justify-self-auto md:mr-8"
          >
            <img src="/logo-A.png" alt={APP_NAME} className="h-5 w-auto" />
          </Link>

          {/* Navigation Links */}
          <nav className="hidden md:flex items-center gap-2 ml-auto">
            <Link
              to="/workbench"
              className="px-3 py-2 text-base text-gray-700 rounded-lg hover:underline hover:underline-offset-4 transition-colors"
            >
              数据工作台
            </Link>
            <Link
              to="/subscribe"
              className="px-3 py-2 text-base text-gray-700 rounded-lg hover:underline hover:underline-offset-4 transition-colors"
            >
              订阅
            </Link>
            <Link
              to="/feedback"
              className="px-3 py-2 text-base text-gray-700 rounded-lg hover:underline hover:underline-offset-4 transition-colors"
            >
              建议反馈
            </Link>
            <a
              href={`${DOCS_URL}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 text-base text-gray-700 rounded-lg hover:underline hover:underline-offset-4 transition-colors"
            >
              文档
            </a>
          </nav>

          {/* Right: Auth Section */}
          <div className="flex items-center gap-3 justify-self-end md:ml-6 md:justify-self-auto">
            {isAuthenticated && user ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-primary-50 transition-colors"
                >
                  <div className="bg-blue-100 rounded-full p-2">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="hidden md:block text-sm font-medium text-gray-700">
                    {user.username || user.nickName || user.phone || user.email || "用户"}
                  </span>
                </button>

                {isUserMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsUserMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-warm-taupe py-1 z-20">
                      <Link
                        to="/dashboard"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-primary-50"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <User className="w-4 h-4" />
                        控制台
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-error-600 hover:bg-primary-50"
                      >
                        <LogOut className="w-4 h-4" />
                        退出登录
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/auth/register")}
                  className="hover:bg-primary-50"
                >
                  注册
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate("/auth/login")}
                >
                  登录
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
